import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, memo, useState } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import {
  ChatType,
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  ProxyDocumentTypes,
  AnalysisTypes,
  queryKeys,
} from '@/app/utils/constants';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import {
  AuctionIcon,
  Refresh01Icon,
  ScrollIcon,
  DownloadIcon,
  SparklesIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { Sidenav, SidenavItem, SidenavLink, SidenavList } from '@/app/ui/components/sidenav';
import { ScrollSpy, ScrollSpySection, ScrollSpyViewport } from '@/app/ui/components/scroll-spy';
import { Spinner } from '@/app/ui/components/spinner';
import {
  useBidDraftQuery,
  useRefreshBidDraft,
  useBidDraftMutation,
} from '@/app/components/features/evaluations/analysis/BidDrafting/useBidDraft';
import { FloatingAlert } from '@/app/components/shared/FloatingAlert';
import { BidDraftingDialog } from '@/app/components/features/bids/BidDraftingDialog';
import { useCheckAnalysisStatus } from '@/app/hooks/useCheckAnalysisStatus';
import AIChatProvider from '@/app/components/features/aiChat/AIChatContext';
import { Main, MainHeader, MainHeaderBackButton } from '@/app/ui/components/main';
import { Sidechat } from '@/app/components/shared/Sidechat';
import useEntryChat from '@/app/components/features/aiChat/hooks/useEntryChat';
import { H1, H2, H3 } from '@/app/ui/components/heading';
import { exportBidDraftToDocx } from '@/app/components/features/evaluations/analysis/BidDrafting/exportBidDraftToDocx';
import { toastManager } from '@/app/ui/components/toast';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useGetAllBidDocuments } from '@/app/hooks/queries/useBids';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/app/ui/components/empty';
import { BidsIcon } from '@/app/components/shared/EmptyIcons';
import { Button } from '@/app/ui/components/button';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Group, GroupItem, GroupSeparator } from '@/app/ui/components/group';
import NewBidDraftingMarkdown from '@/app/components/features/evaluations/analysis/BidDrafting/NewBidDraftingMarkdown';
import { Alert, AlertDescription } from '@/app/ui/components/alert';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import { useGetAllBidsIndexingStatus } from '@/app/hooks/useAllBidsIndexingStatus';
import { Progress, ProgressTrack, ProgressValue } from '@/app/ui/components/progress';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/draft',
)({
  component: RouteComponent,
});

type BidDraftSubsectionBase = {
  id: string;
  title: string;
  text: string;
  previousBidsRationale?: string;
  tenderContextRationale?: string;
  historicalFeedbackRationale?: string;
};

type BidDraftRequirement = {
  id: string;
  statement: string;
  bidDraftSubsections?: BidDraftSubsectionBase[];
};

type FlatBidDraftSubsection = BidDraftSubsectionBase & {
  _requirementStatement: string;
  _tenderCriteriaSection: string;
};

type FlatBidDraftSection = {
  id: string;
  sectionNumber: number;
  tenderCriteriaSection: string;
  tenderCriteriaDocId?: string;
  tenderCriteriaDocName?: string;
  requirements?: BidDraftRequirement[];
  allSubsections: FlatBidDraftSubsection[];
};

type FlatBidDraft = {
  status?: number;
  progress?: number;
  sections: FlatBidDraftSection[];
  id?: string;
};

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tBidDrafting = useTranslations('components.bidDrafting');
  const tGeneral = useTranslations('general');
  const queryClient = useQueryClient();
  const { tenderPackageId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: bidDraftRaw, isLoading: isStatusQueryLoading } = useBidDraftQuery({
    tenderId: tenderPackageId,
    enabled: true,
  });

  const { isCompleted, isProcessing } = useCheckAnalysisStatus({
    status: bidDraftRaw?.status,
    analysisType: AnalysisTypes.BID_DRAFTING,
    refreshParams: { tenderId: tenderPackageId },
    refreshFn: useRefreshBidDraft,
    isQueryLoading: isStatusQueryLoading,
    isMutationPending: false,
  });

  const bidDraft = useMemo<FlatBidDraft | undefined>(() => {
    if (!bidDraftRaw) return bidDraftRaw;

    const extractNumberFromTitle = (title: string): number[] => {
      const match = title.match(/^(\d+(?:\.\d+)*)/);
      if (!match) return [0];
      return match[1].split('.').map((p) => parseInt(p, 10));
    };

    const sortedSections = [...bidDraftRaw.sections].sort(
      (a, b) => a.sectionNumber - b.sectionNumber,
    );

    const processedSections: FlatBidDraftSection[] = sortedSections.map((section) => {
      const allSubsections: FlatBidDraftSubsection[] =
        section.requirements?.flatMap((req) =>
          (req.bidDraftSubsections || []).map((subsection) => ({
            ...subsection,
            _requirementStatement: req.statement,
            _tenderCriteriaSection: section.tenderCriteriaSection,
          })),
        ) || [];

      const sortedSubsections = allSubsections.sort((a, b) => {
        const aParts = extractNumberFromTitle(a.title);
        const bParts = extractNumberFromTitle(b.title);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          if (aVal !== bVal) return aVal - bVal;
        }

        return 0;
      });

      return {
        ...section,
        allSubsections: sortedSubsections,
      } as FlatBidDraftSection;
    });

    return { ...(bidDraftRaw as any), sections: processedSections } as FlatBidDraft;
  }, [bidDraftRaw]);

  const download = useMutation({
    mutationFn: async () => {
      if (!bidDraft) return;
      const fileName = evaluation?.name ? `${evaluation.name} bid draft` : 'Bid draft';
      await exportBidDraftToDocx(bidDraft as any, fileName);
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: tBidDrafting('messages.exportFailed'),
      });
    },
  });

  const stream = useEntryChat({
    id: tenderPackageId,
    namespace: ChatType.RFP,
  });

  const allBidDocuments = useGetAllBidDocuments(tenderPackageId);
  const { data: documents } = useEntityDocuments(tenderPackageId, DocumentTypes.TENDER_PACKAGE);
  const hasTenderPackageDocs = documents && documents.length > 0;
  const { data: tpIndexingStatus, isLoading: isLoadingTPIndexing } = useDocumentsIndexingStatus({
    documentType: ProxyDocumentTypes.TENDER_PACKAGE,
    id: tenderPackageId,
    enabled: hasTenderPackageDocs,
  });
  const tpIndexed = (tpIndexingStatus?.indexedPercentage ?? 0) === 100;

  const { allBidsIndexed, isLoading: isLoadingBidsIndexing } =
    useGetAllBidsIndexingStatus(tenderPackageId);

  const areDocumentsIndexed = tpIndexed && allBidsIndexed;
  const isLoadingIndexingStatus = isLoadingTPIndexing || isLoadingBidsIndexing;

  // Stale analysis handling
  const versionStatus = bidDraftRaw?.versionStatus;
  const { mutate: retriggerBidDraft, isPending: isRetriggering } =
    useBidDraftMutation(tenderPackageId);

  useEffect(() => {
    if (versionStatus === 'stale') {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.INDEXING_STATUS, ProxyDocumentTypes.TENDER_PACKAGE, tenderPackageId],
      });
    }
  }, [versionStatus, queryClient, tenderPackageId]);

  const progress = bidDraftRaw?.progress ?? 0;
  const showContent =
    !isStatusQueryLoading && isCompleted && !!bidDraft && bidDraft?.sections.length > 0;

  return (
    <AIChatProvider
      chatType={ChatType.RFP}
      sourceStream={stream}
      chatInitialOpen={false}
      evaluationName={evaluation.name}
      documents={allBidDocuments}
      documentsPending={isLoadingIndexingStatus}
      areDocumentsIndexed={areDocumentsIndexed}
    >
      <Main className="@container">
        <Header>
          <HeaderBackButton />
          <HeaderSidebarTrigger />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbMenu>
                <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                  <ProjectTypeIcon type={project.type} />
                  {project.name}
                </BreadcrumbMenuItem>
              </BreadcrumbMenu>
              <BreadcrumbSeparator className="max-md:hidden" />
              <BreadcrumbItem>
                <BreadcrumbLink
                  to="/project/$projectId/tender-packages/$tenderPackageId"
                  params={{ projectId, tenderPackageId }}
                >
                  <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                  <span className="line-clamp-1">{evaluation.name}</span>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="max-md:hidden" />
              <BreadcrumbItem className="max-md:hidden">
                <BreadcrumbLink
                  to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                  params={{ projectId, tenderPackageId }}
                >
                  <HugeiconsIcon icon={AuctionIcon} />
                  {t('title')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <HugeiconsIcon icon={ScrollIcon} />
                  {tBidDrafting('title')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <HeaderSidechatButton />
          <HeaderMobileChatButton />
        </Header>
        {showContent ? (
          <MainHeader className="border-b max-md:flex">
            <H1 className="max-md:hidden">
              <MainHeaderBackButton />
              {tBidDrafting('title')}
            </H1>
            <Group className="max-md:w-full">
              <GroupItem
                render={
                  <Button
                    kind="with-icon"
                    variant="secondary"
                    className="max-md:w-full"
                    pending={download.isPending}
                    onClick={() => download.mutate()}
                  >
                    <HugeiconsIcon icon={DownloadIcon} />
                    {tBidDrafting('actions.download')}
                  </Button>
                }
              />
              <GroupSeparator />
              <GroupItem
                render={
                  <Button
                    kind="with-icon"
                    variant="secondary"
                    className="max-md:w-full"
                    onClick={() => setDialogOpen(true)}
                  >
                    <HugeiconsIcon icon={Refresh01Icon} />
                    {tBidDrafting('actions.redraft')}
                  </Button>
                }
              />
            </Group>
          </MainHeader>
        ) : null}
        <ScrollSpy
          scrollContainer={scrollContainer}
          defaultValue={bidDraft?.sections?.[0]?.id}
          className="@4xl:flex-row flex-col"
        >
          {showContent ? (
            <>
              <Sidenav className="border-b xl:w-auto lg:w-auto @4xl:border-b-0 @4xl:border-r @5xl:w-70 @4xl:w-60">
                <SidenavList className="lg:flex-row @4xl:flex-col">
                  {bidDraft?.sections?.map((section) => (
                    <SidenavItem key={section.id}>
                      <SidenavLink value={section.id}>
                        <span className="@4xl:line-clamp-1">
                          {section.tenderCriteriaSection ?? 'Untitled section'}
                        </span>
                      </SidenavLink>
                    </SidenavItem>
                  ))}
                </SidenavList>
              </Sidenav>
              <ScrollSpyViewport viewportRef={setScrollContainer}>
                <div className="max-w-5xl w-full mx-auto p-6">
                  {bidDraft?.sections?.map((section) => (
                    <SectionItem section={section} key={section.id} />
                  ))}
                </div>
              </ScrollSpyViewport>
            </>
          ) : (
            <>
              {isStatusQueryLoading && <Spinner kind="inset" />}
              {!isStatusQueryLoading && !isProcessing && (
                <Empty>
                  <EmptyMedia>
                    <BidsIcon />
                  </EmptyMedia>
                  <EmptyContent>
                    <EmptyTitle>{tBidDrafting('empty.title')}</EmptyTitle>
                    <EmptyDescription>{tBidDrafting('empty.description')}</EmptyDescription>
                    <Button kind="with-icon" onClick={() => setDialogOpen(true)}>
                      <HugeiconsIcon icon={SparklesIcon} />
                      {tBidDrafting('draftBidPlan')}
                    </Button>
                  </EmptyContent>
                </Empty>
              )}
              {!isStatusQueryLoading && isProcessing && (
                <Empty>
                  <EmptyMedia>
                    <BidsIcon />
                  </EmptyMedia>
                  <EmptyContent>
                    <EmptyTitle>{tBidDrafting('empty.title')}</EmptyTitle>
                    <Progress value={+progress.toFixed(0)} max={100} className={''}>
                      <EmptyDescription>
                        {tBidDrafting('draftingBid')} <ProgressValue />
                      </EmptyDescription>
                      <ProgressTrack
                        className={'md:absolute md:bottom-0 md:rounded-none md:left-0 w-full'}
                      />
                    </Progress>
                  </EmptyContent>
                </Empty>
              )}
            </>
          )}
        </ScrollSpy>
        <BidDraftingDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          tenderPackageId={tenderPackageId}
        />
      </Main>
      <Sidechat />
      {versionStatus === 'stale' && !isProcessing && (
        <FloatingAlert
          type="warning"
          title={tGeneral('staleAnalysisAlert.title')}
          description={tGeneral('staleAnalysisAlert.description')}
          actionLabel={tGeneral('staleAnalysisAlert.action')}
          actionDisabled={!areDocumentsIndexed}
          actionPending={isRetriggering}
          onActionClick={() => {
            retriggerBidDraft(undefined);
          }}
        />
      )}
    </AIChatProvider>
  );
}

function SectionItemC({ section }: { section: FlatBidDraftSection }) {
  const subsections = section.allSubsections ?? [];

  return (
    <ScrollSpySection value={section.id}>
      <article className="pb-8 @4xl:pb-16">
        <header className="flex justify-between pb-5 border-b mb-6">
          <H2>{section.tenderCriteriaSection ?? 'Untitled section'}</H2>
        </header>

        <div className="space-y-6">
          {subsections.map((subsection) => {
            const sanitizedText = subsection.text
              .replaceAll('<strong>', '**')
              .replaceAll('</strong>', '**')
              .replace(/<br\s*\/?>/gi, '\n');

            return (
              <div key={subsection.id} className="flex flex-col gap-2">
                <H3>{subsection.title}</H3>
                <Alert variant="info" className="mb-5">
                  <HugeiconsIcon icon={SparklesIcon} />
                  <AlertDescription>{subsection.tenderContextRationale}</AlertDescription>
                </Alert>
                <NewBidDraftingMarkdown text={sanitizedText} />
              </div>
            );
          })}
        </div>
      </article>
    </ScrollSpySection>
  );
}

const SectionItem = memo(SectionItemC);
